import type { GlobalSearchResult } from 'cor-business'

export interface SearchResultWorkspace {
    id: string | number
    [key: string]: any
}

export class SearchResultNavigationError extends Error {
    code: 'missing-project' | 'workspace-unavailable'

    constructor(code: SearchResultNavigationError['code']) {
        super(code)
        this.name = 'SearchResultNavigationError'
        this.code = code
    }
}

export function getSearchResultProjectId(result: GlobalSearchResult) {
    if (result.type === 'project') {
        return result.id
    }

    const structuredProjectId = result.type === 'task'
        ? result.taskDetails?.project?.id
        : result.type === 'document'
            ? result.documentDetails?.projectId
            : null

    if (structuredProjectId != null) {
        return structuredProjectId
    }

    const match = result.href?.match(/\/projects\/([^/?#]+)/)
    return match?.[1] ? decodeURIComponent(match[1]) : null
}

const waitForWorkspaceRender = () => new Promise<void>((resolve) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        resolve()
        return
    }

    window.requestAnimationFrame(() => resolve())
})

async function runAccessCheck<T>(operation: () => Promise<T>): Promise<T> {
    try {
        return await operation()
    } catch (error: any) {
        const status = Number(error?.response?.status ?? error?.status)

        if (status === 401 || status === 403 || status === 404) {
            throw new SearchResultNavigationError('workspace-unavailable')
        }

        throw error
    }
}

export async function navigateToSearchResult<TWorkspace extends SearchResultWorkspace>({
    result,
    currentWorkspaceId,
    workspaces,
    loadProject,
    setWorkspace,
    navigate,
    verifyAccess,
    reloadWorkspaces,
    waitForWorkspaceChange = waitForWorkspaceRender,
}: {
    result: GlobalSearchResult
    currentWorkspaceId?: string | number | null
    workspaces: TWorkspace[]
    loadProject: (projectId: string | number) => Promise<any>
    setWorkspace: (workspace: TWorkspace) => void
    navigate: (href: string) => void
    verifyAccess?: () => Promise<any>
    // The cached workspace list can predate a recent invite, so a missing
    // target is refetched once before navigation is reported as unavailable.
    reloadWorkspaces?: () => Promise<TWorkspace[]>
    waitForWorkspaceChange?: () => Promise<void>
}) {
    if (!result.href) {
        throw new SearchResultNavigationError('missing-project')
    }

    if (verifyAccess) {
        await runAccessCheck(verifyAccess)
    }

    const projectId = getSearchResultProjectId(result)
    let targetWorkspaceId = result.workspaceId

    if (targetWorkspaceId == null) {
        if (projectId == null) {
            throw new SearchResultNavigationError('missing-project')
        }

        const projectResponse = await runAccessCheck(() => loadProject(projectId))
        const project = projectResponse?.project || projectResponse
        targetWorkspaceId = project?.workspace_id
            ?? project?.space_id
            ?? project?.workspaceId
            ?? project?.spaceId
            ?? project?.workspace?.id
            ?? project?.space?.id
    }

    if (targetWorkspaceId == null) {
        throw new SearchResultNavigationError('workspace-unavailable')
    }

    if (String(targetWorkspaceId) !== String(currentWorkspaceId)) {
        const findTargetWorkspace = (list: TWorkspace[]) => list.find(
            (workspace) => String(workspace.id) === String(targetWorkspaceId)
        )

        let targetWorkspace = findTargetWorkspace(workspaces)

        if (!targetWorkspace && reloadWorkspaces) {
            const refreshedWorkspaces = await runAccessCheck(reloadWorkspaces)
            targetWorkspace = findTargetWorkspace(refreshedWorkspaces || [])
        }

        if (!targetWorkspace) {
            throw new SearchResultNavigationError('workspace-unavailable')
        }

        setWorkspace(targetWorkspace)
        await waitForWorkspaceChange()
    }

    navigate(result.href)
}
